import type { InvestmentAccountReturns } from 'shared';

import { useAnnualReturnsForAccounts } from '../../features/analytics/queries';
import { mandateLabel } from '../../lib/accountMandate';
import type { AccountDto } from '../../lib/api/endpoints';

import { AnnualReturnsTable, type AnnualReturnsRow } from './AnnualReturnsTable';

/**
 * Calendar-year returns per account. The mandate decides the source: long-term accounts use
 * their imported holdings valuations; every other account uses its NAV snapshots.
 */
export function AccountAnnualReturns({
  accounts,
  longTermReturns,
  longTermState,
  label,
}: {
  accounts: readonly AccountDto[];
  longTermReturns: readonly InvestmentAccountReturns[] | undefined;
  longTermState: 'ready' | 'loading' | 'error';
  label: string;
}) {
  const navAccounts = accounts.filter((account) => account.mandate !== 'LONG_TERM_INVESTING');
  const navQueries = useAnnualReturnsForAccounts(navAccounts.map((account) => account.id));

  const rows: AnnualReturnsRow[] = accounts.map((account) => {
    const base = {
      accountId: account.id,
      accountName: account.name,
    };
    const mandate = account.mandate ? mandateLabel(account.mandate) : 'Unassigned';
    if (account.mandate === 'LONG_TERM_INVESTING') {
      return {
        ...base,
        detail: `${mandate} · ${account.currency} · holdings valuations`,
        state: longTermState,
        years:
          longTermReturns?.find((returns) => returns.accountId === account.id)?.calendarYears ?? [],
      };
    }
    const query = navQueries[navAccounts.findIndex((item) => item.id === account.id)];
    return {
      ...base,
      detail: `${mandate} · ${account.currency} · NAV snapshots`,
      state: query?.isError ? 'error' : query?.data ? 'ready' : 'loading',
      years: query?.data?.years ?? [],
    };
  });

  return <AnnualReturnsTable rows={rows} label={label} />;
}
